import React, {useCallback, useEffect, useState} from 'react';
import {useHistory} from 'react-router';

import {
	Asset,
	AssetGroup,
	Datapoint,
	fetchDatapointsByAssetId,
	PaginationSettings
} from '@deviceinsight/ng-ui-api-client';
import {useConfirm} from '@deviceinsight/ng-ui-basic-components';
import {
	ContentHeader,
	MultiMenu,
	MultiMenuItem,
	ReactableWithClientSidePagination
} from '@deviceinsight/ng-ui-components';

import {HasAuthorities, Trans, useTranslation} from '@deviceinsight/ng-ui-scale-lib/api';
import {getSharedWithAssetGroupsColumnDef, getStaticColumnDefs, getUtilitiesButtonColumnDef} from './columnDefinitions';
import HeaderToolbar from '../../components/HeaderToolbar';
import ShareDialog from './ShareDialog';
import SingleDataProcessingDefinitionSelectionTableModal from './SingleDataProcessingDefinitionSelectionTableModal';
import {
	assignDPDToAsset,
	displayErrorMessage,
	displaySuccessMessage,
	getDataProcessingDefinitionValidationErrors,
	getDuplicatedDefinitionBody,
	getMissingDatapointsPerDPD,
	getUniqueKPIs,
	removeDPDFromAsset,
	sort
} from './utils';
import {AssignedAssetGroupsPerDPD, DataProcessingDefinitionValidationErrors, MissingDatapointsPerDPD} from './types';
import makeCancelable from '../../utils/makeCancelable';
import {
	assignDataProcessingDefinitionsToAssetGroup,
	createDataProcessingDefinition,
	DataProcessingDefinition,
	fetchDataProcessingDefinitionsAssignedToAsset,
	fetchDataProcessingDefinitionsAssignedToAssetGroup,
	listDataProcessingDefinitionAssetGroupAssignments,
	AssetGroupAssignment
} from '../../api/dataProcessingDefinitionApi';
import {AUTHORITY_CREATE_KPI_DATA_PROCESSING_DEFINITION} from '../common/consts';

const INITITAL_PAGINATION_SETTINGS: PaginationSettings = {
	page: 0,
	size: 10,
	sort: {
		property: 'kpiName',
		direction: 'ASC'
	}
};

const HEADER_TITLE = {
	id: 'assets.detail.dataProcessingDefinition.create.title',
	defaultValue: 'Data Processing Definitions'
};

const messageSuccessShareKPI = (
	<Trans i18nKey="assets.detail.dataProcessingDefinition.shareSuccessMessage">
		Data Processing Definition has been shared successfully.
	</Trans>
);

const messageSuccessUnshareKPI = (
	<Trans i18nKey="assets.detail.dataProcessingDefinition.unshareSuccessMessage">
		Data Processing Definition has been unshared successfully.
	</Trans>
);

const messageSuccessUnassignKPI = (
	<Trans i18nKey="assets.detail.dataProcessingDefinition.unassignSuccessMessage">
		Data Processing Definition was successfully unassigned from this Asset.
	</Trans>
);

const messageSuccessAssignExistingKPI = (
	<Trans i18nKey="assets.detail.dataProcessingDefinition.assignExistingKPISuccessMessage">
		Existing Data Processing Definition was successfully assigned.
	</Trans>
);

const messageSuccessDuplicateExistingKPI = (
	<Trans i18nKey="assets.detail.dataProcessingDefinition.duplicateExistingKPISuccessMessage">
		Existing Data Processing Definition was successfully duplicated.
	</Trans>
);

type AddOperation = 'duplicate' | 'existing';

type Props = {
	asset: Asset;
};

export default function AssetDataProcessingDefinitionList({asset: {id: assetId, assetGroupIds}}: Props) {
	const history = useHistory();
	const [dataProcessingDefinitions, setDataProcessingDefinitions] = useState<Array<DataProcessingDefinition>>([]);
	const [assignedAssetGroupsPerDPD, setAssignedAssetGroupsPerDPD] = useState<AssignedAssetGroupsPerDPD>({});
	const [missingDatapointsPerDPD, setMissingDatapointsPerDPD] = useState<MissingDatapointsPerDPD>({});
	const [selectedDataProcessingDefinition, setSelectedDataProcessingDefinition] = useState<
		DataProcessingDefinition | null | undefined
	>(undefined);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [datapoints, setDatapoints] = useState<Datapoint[]>([]);
	const [isSharing, setIsSharing] = useState<boolean>(false);
	const [dpdValidationErrorsByAssetGroup, setDpdValidationErrorsByAssetGroup] =
		useState<DataProcessingDefinitionValidationErrors>({});
	const [addOperationType, setAddOperationType] = useState<AddOperation | null | undefined>(null);
	const [isAddingDpds, setIsAddingDpds] = useState<boolean>(false);

	const getAssignedAssetGroupsPerDPD = useCallback(
		async (dataProcessingDefinitions: Array<DataProcessingDefinition>): Promise<AssignedAssetGroupsPerDPD> => {
			if (!dataProcessingDefinitions.length) return {} as AssignedAssetGroupsPerDPD;

			const dpdIds = dataProcessingDefinitions.map(({id}) => id);
			const assignments = await listDataProcessingDefinitionAssetGroupAssignments(dpdIds);

			// 2) Build AssignedAssetGroupsPerDPD directly from assignments, filtered by current asset's assetGroupIds
			const result: AssignedAssetGroupsPerDPD = {};
			for (const assignment of assignments) {
				const dpdId = assignment.id;
				const filteredAssignments = assignment.assignedToAssetGroups.filter((g) =>
					assetGroupIds.includes(g.assetGroupId)
				);
				result[dpdId] = filteredAssignments;
			}

			// Ensure all DPDs have an entry, even if empty
			for (const {id: dpdId} of dataProcessingDefinitions) {
				if (!result[dpdId]) {
					result[dpdId] = [];
				}
			}

			return result;
		},
		[assetGroupIds]
	);
	const {t} = useTranslation();
	const confirm = useConfirm();

	const fetchAssetDPDs = useCallback(() => {
		setIsLoading(true);
		const {promise, cancel} = makeCancelable(fetchDataProcessingDefinitionsAssignedToAsset(assetId));
		promise.then((dataProcessingDefinitions) => {
			const uniqueKPIs = getUniqueKPIs(dataProcessingDefinitions);
			setDataProcessingDefinitions(uniqueKPIs);
			setIsLoading(false);
		});
		return cancel;
	}, [assetId]);

	useEffect(() => {
		return fetchAssetDPDs();
	}, [assetId, fetchAssetDPDs]);

	useEffect(() => {
		setIsLoading(true);
		fetchDatapointsByAssetId(assetId).then(setDatapoints);
	}, [assetId]);

	useEffect(
		() => {
			const uniqueKPIs = getUniqueKPIs(dataProcessingDefinitions);
			const assignedAssetGroupsPerDPD = getAssignedAssetGroupsPerDPD(uniqueKPIs);
			const missingDatapointsPerDPD = getMissingDatapointsPerDPD(uniqueKPIs, datapoints);
			const {promise, cancel} = makeCancelable(Promise.all([assignedAssetGroupsPerDPD, missingDatapointsPerDPD]));
			promise.then(([assignedAssetGroups, missingDatapoints]) => {
				setDataProcessingDefinitions(uniqueKPIs);
				setAssignedAssetGroupsPerDPD(assignedAssetGroups);
				setMissingDatapointsPerDPD(missingDatapoints);
			});
			return cancel;
		}, // only execute on different array length
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dataProcessingDefinitions.length, datapoints.length, getAssignedAssetGroupsPerDPD]
	);

	const handleOnFailedSharing = (error: any, assetGroupId: number) => {
		const dataProcessingDefinitionValidationErrors = getDataProcessingDefinitionValidationErrors(error);
		setDpdValidationErrorsByAssetGroup((dpdErrors) => ({
			...dpdErrors,
			[assetGroupId]: dataProcessingDefinitionValidationErrors
		}));
	};

	const updateSharedDPDs = (
		assetGroupId: number,
		dataProcessingDefinitionId: number,
		getNextDPDs: (assignedDPDs: Array<DataProcessingDefinition>) => Array<number>,
		getNextAssignedAssetGroups: (
			oldAssignedAssetGroups: Array<AssetGroupAssignment>
		) => Array<AssetGroupAssignment>,
		successMessage: JSX.Element
	) => {
		fetchDataProcessingDefinitionsAssignedToAssetGroup(assetGroupId)
			.then((assignedDataProcessingDefinitions) =>
				assignDataProcessingDefinitionsToAssetGroup(
					assetGroupId,
					getNextDPDs(assignedDataProcessingDefinitions)
				)
			)
			.then(() => fetchDataProcessingDefinitionsAssignedToAsset(assetId))
			.then(getUniqueKPIs)
			.then((assignedDataProcessingDefinitions) => {
				const oldAssignedAssetGroups = assignedAssetGroupsPerDPD[dataProcessingDefinitionId];
				const nextAssignedAssetGroups = getNextAssignedAssetGroups(oldAssignedAssetGroups);
				setAssignedAssetGroupsPerDPD({
					...assignedAssetGroupsPerDPD,
					[dataProcessingDefinitionId]: nextAssignedAssetGroups
				});
				setDataProcessingDefinitions(assignedDataProcessingDefinitions);
			})
			.then(() => {
				displaySuccessMessage(successMessage);
				setIsSharing(false);
			})
			.catch((error) => handleOnFailedSharing(error, assetGroupId));
	};

	const getColumnDefs = () => {
		const openUnassignDialogWithDPD = async (dataProcessingDefinition: DataProcessingDefinition) => {
			const {
				id,
				definition: {kpiName}
			} = dataProcessingDefinition;
			const confirmed = await confirm({
				title: t('assets.detail.dataProcessingDefinition.unassignConfirmTitle', {
					defaultValue: 'Unassign Data Processing Definition'
				}),
				message: (
					<Trans i18nKey="assets.detail.dataProcessingDefinition.unassignConfirmMessage">
						Do you really want to unassign <strong>{kpiName}</strong>?
					</Trans>
				)
			});

			if (confirmed) {
				handleUnassignDataProcessingDefinition(id);
			}
		};

		const openUnshareDialogWithDPD = async (
			dataProcessingDefinition: DataProcessingDefinition,
			assetGroupAssignment: AssetGroupAssignment
		) => {
			const {
				id,
				definition: {kpiName}
			} = dataProcessingDefinition;
			const assetGroupName = assetGroupAssignment.assetGroupDisplayName;
			const confirmed = await confirm({
				title: t('assets.detail.dataProcessingDefinition.unshareConfirmTitle', {
					defaultValue: 'Unshare Data Processing Definition'
				}),
				message: (
					<Trans i18nKey="assets.detail.dataProcessingDefinition.unshareFromKnownAssetConfirmMessage">
						Do you want to unassign <strong>{kpiName}</strong> from{' '}
						{{
							assetGroupName
						}}{' '}
						?
					</Trans>
				)
			});

			if (confirmed) {
				handleUnshare(assetGroupAssignment.assetGroupId, id);
			}
		};

		const openShareDialogWithDPD = (selectedDataProcessingDefinition: DataProcessingDefinition) => {
			setIsSharing(true);
			setSelectedDataProcessingDefinition(selectedDataProcessingDefinition);
		};

		return [
			...getStaticColumnDefs(missingDatapointsPerDPD),
			getSharedWithAssetGroupsColumnDef(assignedAssetGroupsPerDPD, openUnshareDialogWithDPD),
			getUtilitiesButtonColumnDef(openUnassignDialogWithDPD, openShareDialogWithDPD)
		];
	};

	const handleShare = (assetGroup: AssetGroup, dataProcessingDefinitionId: number) => {
		const {id: assetGroupId} = assetGroup;
		updateSharedDPDs(
			assetGroupId,
			dataProcessingDefinitionId,
			(assignedDataProcessingDefinitions) => [
				...assignedDataProcessingDefinitions.map(({id}) => id),
				dataProcessingDefinitionId
			],
			(oldAssignedAssetGroups) => [
				...oldAssignedAssetGroups,
				{
					assetGroupId: assetGroup.id,
					assetGroupName: assetGroup.name,
					assetGroupDisplayName: assetGroup.displayName || assetGroup.name
				}
			],
			messageSuccessShareKPI
		);
	};

	const handleUnshare = (assetGroupId: number, dataProcessingDefinitionId: number) => {
		updateSharedDPDs(
			assetGroupId,
			dataProcessingDefinitionId,
			(assignedDataProcessingDefinitions) =>
				assignedDataProcessingDefinitions.map(({id}) => id).filter((id) => id !== dataProcessingDefinitionId),
			(oldAssignedAssetGroups) => oldAssignedAssetGroups.filter(({assetGroupId: agId}) => agId !== assetGroupId),
			messageSuccessUnshareKPI
		);
	};

	const handleUnassignDataProcessingDefinition = (definitionId: number) => {
		setIsLoading(true);
		removeDPDFromAsset(definitionId, assetId)
			.then(() => displaySuccessMessage(messageSuccessUnassignKPI))
			.catch(displayErrorMessage)
			.then(fetchAssetDPDs);
	};

	const filterSharedDataProcessingDefinitions = (selectedDataProcessingDefinition: DataProcessingDefinition) => {
		const {id} = selectedDataProcessingDefinition;
		const assignedAssetGroupsToSelectedDPD = assignedAssetGroupsPerDPD[id];
		return assetGroupIds.filter(
			(id) => !assignedAssetGroupsToSelectedDPD.find((assetGroup) => assetGroup.assetGroupId === id)
		);
	};

	const closeShareDialog = () => {
		setDpdValidationErrorsByAssetGroup({});
		setIsSharing(false);
	};

	const handleCreateNew = () => {
		history.push('dataprocessingdefinitions/create');
	};

	const handleCreateDuplicateFromExisting = () => {
		setAddOperationType('duplicate');
		setIsAddingDpds(true);
	};

	const handleAddDirectAssignmentToExisting = () => {
		setAddOperationType('existing');
		setIsAddingDpds(true);
	};

	const handleSelectDataProcessingDefinition = async (selectedDefinition: DataProcessingDefinition) => {
		switch (addOperationType) {
			case 'duplicate': {
				const duplicatedDefinitionBody = getDuplicatedDefinitionBody(selectedDefinition);
				const createdDefinition = await createDataProcessingDefinition({
					definition: duplicatedDefinitionBody,
					assetIdsWithAccess: [assetId]
				});
				await assignDPDToAsset(createdDefinition.id, assetId);
				displaySuccessMessage(messageSuccessDuplicateExistingKPI);
				break;
			}

			case 'existing': {
				await assignDPDToAsset(selectedDefinition.id, assetId);
				displaySuccessMessage(messageSuccessAssignExistingKPI);
				break;
			}

			default:
				throw new Error(`Invalid addOperationType: ${addOperationType || ''}`);
		}

		setAddOperationType(null);
		setIsAddingDpds(false);
		fetchAssetDPDs();
	};

	const closeAddDpdDialog = () => {
		setAddOperationType(null);
		setIsAddingDpds(false);
	};

	return (
		<div>
			<ContentHeader title={HEADER_TITLE}>
				<HeaderToolbar>
					<HasAuthorities authorities={[AUTHORITY_CREATE_KPI_DATA_PROCESSING_DEFINITION] as string[]}>
						<MultiMenu menuButton={<button className="tool add" />}>
							<MultiMenuItem onClick={handleCreateNew}>
								<Trans i18nKey="assets.detail.dataProcessingDefinition.createBlankNew">
									Create blank new
								</Trans>
							</MultiMenuItem>
							<MultiMenuItem onClick={handleCreateDuplicateFromExisting}>
								<Trans i18nKey="assets.detail.dataProcessingDefinition.createDuplicateFromExisting">
									Create duplicate from existing
								</Trans>
							</MultiMenuItem>
							<MultiMenuItem onClick={handleAddDirectAssignmentToExisting}>
								<Trans i18nKey="assets.detail.dataProcessingDefinition.addDirectAssignmentToExisting">
									Add a direct assignment to an existing
								</Trans>
							</MultiMenuItem>
						</MultiMenu>
					</HasAuthorities>
				</HeaderToolbar>
			</ContentHeader>
			<ReactableWithClientSidePagination
				columnDefs={getColumnDefs()}
				uniqueItemKey={(item) => item.id}
				paginationSettings={INITITAL_PAGINATION_SETTINGS}
				items={dataProcessingDefinitions}
				isLoading={isLoading}
				customSortFunction={sort}
			/>
			{isSharing && !!selectedDataProcessingDefinition && (
				<ShareDialog
					dataProcessingDefinition={selectedDataProcessingDefinition}
					assetGroupIds={filterSharedDataProcessingDefinitions(selectedDataProcessingDefinition)}
					dpdValidationErrors={dpdValidationErrorsByAssetGroup}
					hide={closeShareDialog}
					apply={handleShare}
				/>
			)}
			{isAddingDpds && (
				<SingleDataProcessingDefinitionSelectionTableModal
					onSelectDataProcessingDefinition={handleSelectDataProcessingDefinition}
					onOutsideClick={closeAddDpdDialog}
				/>
			)}
		</div>
	);
}
